/** Single accessible tab with title, close button, and dirty indicator. */

import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAppStore, type Tab } from '../../stores/app-store'
import { documentEditPath } from '../../lib/document-route'

interface TabItemProps {
  tab: Tab
  isActive: boolean
}

export function TabItem({ tab, isActive }: TabItemProps) {
  const { setActiveTab, closeTab, openTabs, theme } = useAppStore()
  const navigate = useNavigate()
  const isDark = theme === 'dark'
  const title = tab.title || 'Untitled'

  const activate = () => {
    setActiveTab(tab.id)
    navigate(documentEditPath({ id: tab.documentId }))
  }

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const index = openTabs.findIndex((item) => item.id === tab.id)
    const renderedTabs = Array.from(
      event.currentTarget
        .closest('[role="tablist"]')
        ?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [],
    )
    const adjacentTab =
      renderedTabs.length > 1
        ? renderedTabs[index === renderedTabs.length - 1 ? index - 1 : index + 1]
        : null
    closeTab(tab.id)
    queueMicrotask(() => {
      if (adjacentTab) adjacentTab.focus()
      else document.querySelector<HTMLElement>('[data-shell-content]')?.focus()
    })

    if (!isActive) return
    const remaining = openTabs.filter((item) => item.id !== tab.id)
    if (remaining.length === 0) {
      navigate('/')
      return
    }
    const nextTab = index > 0 ? remaining[index - 1] : remaining[0]
    navigate(documentEditPath({ id: nextTab.documentId }))
  }

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
    const tabs = Array.from(
      event.currentTarget
        .closest('[role="tablist"]')
        ?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [],
    )
    const current = tabs.indexOf(event.currentTarget)
    if (current < 0 || tabs.length === 0) return
    event.preventDefault()
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : event.key === 'ArrowLeft'
            ? (current - 1 + tabs.length) % tabs.length
            : (current + 1) % tabs.length
    tabs[nextIndex].focus()
    tabs[nextIndex].click()
  }

  return (
    <div
      className={cn(
        'group flex h-full max-w-[200px] min-w-[100px] items-center gap-1.5 border-r px-2 text-xs select-none',
        isDark ? 'border-white/[0.04]' : 'border-neutral-200',
        isActive
          ? isDark
            ? 'bg-surface-0 text-neutral-100'
            : 'bg-white text-neutral-900'
          : isDark
            ? 'text-neutral-400 hover:bg-surface-2 hover:text-neutral-200'
            : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800',
      )}
    >
      <button
        type="button"
        role="tab"
        aria-selected={isActive}
        aria-label={`${title}${tab.isDirty ? ', unsaved changes' : ''}`}
        tabIndex={isActive ? 0 : -1}
        onClick={activate}
        onKeyDown={handleTabKeyDown}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 self-stretch text-left"
      >
        {tab.isDirty && (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" aria-hidden="true" />
        )}
        <span className="flex-1 truncate">{title}</span>
      </button>

      <button
        type="button"
        aria-label={`Close ${title}`}
        onClick={handleClose}
        className={cn(
          'aw-editor-tab-close shrink-0 cursor-pointer rounded-md p-1 opacity-60 md:p-0.5 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100',
          isActive && 'opacity-60 md:hover:opacity-100',
          isDark
            ? 'hover:bg-surface-3 hover:text-neutral-200'
            : 'hover:bg-neutral-200 hover:text-neutral-700',
        )}
        title={`Close ${title}`}
      >
        <X className="h-3.5 w-3.5 md:h-3 md:w-3" aria-hidden="true" />
      </button>
    </div>
  )
}
